import { sanityOriginClient } from './client';
import { teamRevealBySlugQuery, teamRevealDraftBySlugQuery } from './queries';
import type { SanityArticlePage } from './types';

/**
 * Reveals nest under the contributor's existing team reveals page rather than
 * taking a flat slug, so the per-author listings at /team-reveals/<author>
 * keep their URLs and a reveal can never collide with an author handle.
 */
export const TEAM_REVEALS_PATH_PREFIX = '/team-reveals';
// Some author slugs arrive wrapped in slashes (a Sanity author saved as
// "/chris_tan/"), which would join into "/team-reveals//chris_tan//...".
const EDGE_SLASHES_PATTERN = /^\/+|\/+$/g;

const trimEdgeSlashes = (segment: string): string =>
  segment.replace(EDGE_SLASHES_PATTERN, '');

export const buildTeamRevealPath = (authorSlug: string, slug: string): string =>
  `${TEAM_REVEALS_PATH_PREFIX}/${trimEdgeSlashes(authorSlug)}/${trimEdgeSlashes(slug)}`;

export const buildTeamRevealListingPath = (authorSlug: string): string =>
  `${TEAM_REVEALS_PATH_PREFIX}/${trimEdgeSlashes(authorSlug)}`;

export const getAllTeamRevealPathsQuery = `
  *[_type == "teamReveal" && defined(slug.current) && defined(author->slug.current)] {
    "slug": slug.current,
    "authorSlug": author->slug.current
  }
`;

export type TeamRevealPathParams = { authorSlug: string; slug: string };

export const getTeamReveal = async ({
  authorSlug,
  slug
}: TeamRevealPathParams): Promise<SanityArticlePage | null> => {
  const page = await sanityOriginClient.fetch<SanityArticlePage | null>(
    teamRevealBySlugQuery,
    { authorSlug, slug }
  );
  return page ?? null;
};

/**
 * Draft-or-published read, premium body included, for the editor preview route
 * only.
 */
export const getTeamRevealDraft = async ({
  authorSlug,
  slug
}: TeamRevealPathParams): Promise<SanityArticlePage | null> => {
  const page = await sanityOriginClient
    .withConfig({ perspective: 'drafts' })
    .fetch<SanityArticlePage | null>(teamRevealDraftBySlugQuery, {
      authorSlug,
      slug
    });
  return page ?? null;
};
